import {
    Component,
    ElementRef,
    Inject,
    NgZone,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
} from '@angular/core';
import { CMPS_DIALOG_DATA, CmpsDialogRef } from '@cmps/ng-uikit';
import { animationFrameScheduler, of, Subject } from 'rxjs';
import {
    auditTime,
    catchError,
    debounceTime,
    distinctUntilChanged,
    retryWhen,
    scan,
    take,
    tap,
} from 'rxjs/operators';

import { CoachingType } from '../../coaching-base/general';
import { TranslationKeyMap } from '../../coaching-insight/coaching-insight.i18n';
import { scrollIntoViewInstant } from '../../coaching-observation/bring-into-focus.service';
import {
    Column,
    Context,
    DivRendererOptions,
    ExperimentalNgGridComponent,
    GridContent,
    SelectTemplateFn,
    SortOrder,
} from '../../coaching-observation/experimental.grid';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../../common/take-until-destroy.decorator';
import { StateToProps } from '../../store';
import { disuseStore, useDispatcher, useStore } from '../../store.decorator';
import { TagItem } from '../models/tag.coaching.model';
import { TagsDialogResult } from '../tags-dialog-result.model';

import {
    tagSetCache,
    tagSetFilterMetricSelectionOptions,
    tagSetFilterSelectionOptions,
    tagSetInit,
    tagSetSelectBehaviour,
    tagSetSelectRecentTag,
    tagSetSelectSubBehaviour,
    tagSetSortGrid,
    tagSetToggleCategory,
    tagSetToggleMetric,
} from './tags.action';
import {
    ColumnID,
    MIN_FILTER_TEXT_LENGTH,
    TagItemCategoryView,
    TagsDialogData,
    TagSetMode,
} from './tags.model';
import tagSetReducer from './tags.reducer';
import { TagSetState } from './tags.state';

// TODO rename to TagSetComponentNext
@Component({
    selector: '[cmpsCoachingTagsNext]',
    templateUrl: './tags.component.html',
    host: {
        'data-e2e': 'component-tags-next',
    },
})
@TakeUntilDestroy()
export class TagsComponent
    implements OnInit, OnDestroy, StateToProps<TagSetState>
{
    state: TagSetState;

    @ViewChild('grid') private readonly _grid: ExperimentalNgGridComponent;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _TagSetMode = TagSetMode;
    /** @private */ _MIN_FILTER_TEXT_LENGTH = MIN_FILTER_TEXT_LENGTH;
    /** @private */ _SortOrder = SortOrder;
    /** @private */ _ColumnID = ColumnID;
    /** @private */ _CoachingType = CoachingType;

    /** @private */ _tagItemViews: TagItemCategoryView[];

    /** @private */ _filterText: string;
    private _filterSubj: Subject<string>;

    /** @private */ _filterMetricText: string;
    private _filterMetricSubj: Subject<string>;

    /** @private */ _rendererOpts: DivRendererOptions;

    /**
     * @private
     * for both initial load and when a recent tag is clicked in order to trigger a scrollIntoView
     */
    _didSelectRecentTag: boolean;

    /** @private */ _selectTpl: SelectTemplateFn = (
        context: Context,
        contents: QueryList<GridContent>,
    ) => {
        if (context.t === 'grid') {
            return contents.find((e) => e.type === 'grid');
        }
        if (context.t === 'row') {
            return contents.find(
                (e) =>
                    e.type === 'row' &&
                    e.id === (!context.ctx ? 'header' : context.ctx.templateId),
            );
        }
        if (context.t === 'cell') {
            return contents.find(
                (e) => e.type === 'cell' && e.id === context.ctx.templateId,
            );
        }
    };

    constructor(
        private readonly _el: ElementRef<HTMLElement>,
        private readonly _ngZone: NgZone,
        private readonly _dialogRef: CmpsDialogRef<
            TagsComponent,
            TagsDialogResult
        >,
        @Inject(CMPS_DIALOG_DATA) /** @private */
        readonly _options: TagsDialogData,
    ) {
        this._didSelectRecentTag = true;
    }

    ngOnInit(): void {
        useStore(
            this,
            `${this._options.storePathPrefix}.tagSet`,
            tagSetReducer.tagSet,
        );

        if (this._options.mode == null) {
            this._options.mode = TagSetMode.DEFAULT;
        }

        this._filterSubj = new Subject();
        this._filterSubj
            .asObservable()
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                tap((text) => {
                    useDispatcher(this, tagSetFilterSelectionOptions({ text }));
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._filterMetricSubj = new Subject();
        this._filterMetricSubj
            .asObservable()
            .pipe(
                debounceTime(250),
                distinctUntilChanged(),
                tap((text) => {
                    useDispatcher(
                        this,
                        tagSetFilterMetricSelectionOptions({ text }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._rendererOpts = {
            columns: this.state.gridColumns,
            defaultRenderFn: null,
            height: 170,
        };

        useDispatcher(
            this,
            tagSetInit({
                performerId: this._options.performerId,
                selected: this._options.selected,
                pinnedMetrics: this._options.pinnedMetrics,
                activeMetricId: this._options.activeMetricId,
                focusSelectionParams: this._options.focusSelectionParams,
            }),
        );
    }

    ngOnDestroy(): void {
        disuseStore(this);

        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._filterSubj && this._filterSubj.complete();
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._filterMetricSubj && this._filterMetricSubj.complete();
    }

    stateToProps(prevState?: TagSetState): void {
        if (!prevState) {
            return;
        }

        if (prevState.selection !== this.state.selection) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._grid && this._grid.gridNode.rowsTop.detectChanges();
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._grid && this._grid.gridNode.detectChanges();

            if (this._didSelectRecentTag) {
                this._didSelectRecentTag = false;

                if (this.state.selection.behaviour) {
                    this._scrollIntoView(
                        `[data-behaviourid="${this.state.selection.behaviour.id}"]`,
                        '[data-behaviourscrollcontainer]', // direct scroll container reference
                    );
                }

                if (this.state.selection.subBehaviour) {
                    this._scrollIntoView(
                        `[data-subbehaviourid="${this.state.selection.subBehaviour.id}"]`,
                        '[data-subbehaviourscrollcontainer]',
                    );
                }
            }
        }

        if (prevState.filterMetricText !== this.state.filterMetricText) {
            this._rendererOpts = {
                ...this._rendererOpts,
            };
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._grid && this._grid.renderer.layout();
        }

        if (prevState.gridColumns !== this.state.gridColumns) {
            this._rendererOpts = {
                ...this._rendererOpts,
                columns: this.state.gridColumns,
            };
        }

        if (prevState.gridSortDefs !== this.state.gridSortDefs) {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._grid &&
                this._grid.renderer.contentColumns.columns.forEach((r) =>
                    r.cells.forEach((l) => l.detectChanges({ onlySelf: true })),
                );
        }

        if (prevState.tagItemCategories !== this.state.tagItemCategories) {
            this._tagItemViews = [
                {
                    title: TranslationKeyMap._9612_ACTIVE_FOCUS_FOR_THIS_PERFORMER,
                    ...this.state.tagItemCategories.active,
                },
                {
                    title: TranslationKeyMap._9611_RECENTLY_CLOSED_FOCUSES_FOR_THIS_PERFORMER,
                    ...this.state.tagItemCategories.closed,
                },
                {
                    title: TranslationKeyMap._9613_RECENT_FOCUS_FOR_TEAM,
                    ...this.state.tagItemCategories.team,
                },
            ];
        }
    }

    propsToDispatch(): void {
        // restore only when the performer is the same
        // restore for the initial load only
        this._filterText =
            this._options.performerId === this.state.performerId
                ? this.state.filterText
                : '';
        this._filterMetricText =
            this._options.performerId === this.state.performerId
                ? this.state.filterMetricText
                : '';
    }

    /** @private */ _onToggleCollapseClick(
        item: TagItemCategoryView,
        index: number,
    ): void {
        useDispatcher(
            this,
            tagSetToggleCategory({
                category: ['active', 'closed', 'team'][index],
                isCollapsed: !item.isCollapsed,
            }),
        );
    }

    /** @private */ _onRecentTagClick(item: TagItem): void {
        this._didSelectRecentTag = true;
        useDispatcher(this, tagSetSelectRecentTag({ tagItem: item }));
    }

    /** @private */ _onFilterKeyUp(text: string): void {
        this._filterSubj.next(text);
    }

    /** @private */ _onFilterKeyUpMetric(text: string): void {
        this._filterMetricSubj.next(text);
    }

    /** @private */ _onBehaviourChange($event: any): void {
        useDispatcher(this, tagSetSelectBehaviour({ behaviourId: $event }));
    }

    /** @private */ _onSubBehaviourChange($event: any): void {
        useDispatcher(
            this,
            tagSetSelectSubBehaviour({ subBehaviourId: $event }),
        );
    }

    /** @private */ _onMetricClick(metricId: number, $event: boolean): void {
        this._ngZone.run(() => {
            useDispatcher(
                this,
                tagSetToggleMetric({ metricId, checked: $event }),
            );
        });
    }

    /** @private */ _onSortClick(column: Column): void {
        this._ngZone.run(() => {
            useDispatcher(this, tagSetSortGrid({ columnId: column.id }));
        });
    }

    /** @private */
    _onSetSelectedFocusClick(): void {
        const run = (): void => {
            if (this._options.mode !== TagSetMode.METRIC_ONLY) {
                useDispatcher(this, tagSetCache({ tags: this.state.tags }));
            }

            this._dialogRef.close({
                selected: this.state.selected,
                activeCommitmentFocuses: this.state.activeUserCommitmentFocuses,
            });
        };

        if (!this._options.canSelectFocus) {
            run();
        } else {
            const ret = this._options.canSelectFocus(this.state.selected);
            if (ret instanceof Promise) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                ret.then((result) => {
                    if (result) {
                        run();
                    }
                });
            }

            if (ret) {
                run();
            }
        }
    }

    private _scrollIntoView(selectorA: string, selectorB: string): void {
        this._ngZone.onStable
            .pipe(
                take(1),
                tap(() => {
                    // use instant to avoid the dialog scroll container from also scrolling ()
                    scrollIntoViewInstant(
                        this._el.nativeElement.querySelector(selectorA),
                        this._el.nativeElement.querySelector(selectorB),
                    );
                }),
                retryWhen((errors) =>
                    errors.pipe(
                        scan((acc) => {
                            if (acc >= 5) {
                                throw new Error('still not found');
                            }
                            return acc + 1;
                        }, 0),
                        auditTime(0, animationFrameScheduler),
                    ),
                ),
                catchError(() => of(null)),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
